import { capDelay, exponentialBackoff } from 'retry-ts';

import type { task } from '@code-expert/prelude';
import { date } from '@code-expert/prelude';
import * as rpc from '@code-expert/rpc-client';
import type { ProcedureArgs, ProcedureName, ProcedureResult } from '@code-expert/rpc-common';
import type { Procedures, ReadOnlyProcedures } from '/imports/api/rpc';
import { socket } from '/imports/startup/client/socket';

type Args<P> = [P] extends [undefined] ? [] : [P];

export const invokeProcedure: <N extends ProcedureName<Procedures>>(
  name: N,
) => (...args: Args<ProcedureArgs<Procedures, N>>) => task.Task<ProcedureResult<Procedures, N>> =
  rpc.invokeProcedure(socket) as $Inexpressible;

// We require this to be present in Playwright e2e tests.
(globalThis as $IntentionalAny).__PLAYWRIGHT_invokeProcedure__ = invokeProcedure;

export const useProcedure = rpc.makeUseProcedure<Procedures>(socket);

export const useCachedProcedure = rpc.makeUseCachedProcedure<Procedures>(socket);

// -------------------------------------------------------------------------------------------------

/**
 * We need to ensure to retry early in case the socket has not been connected yet
 * after startup. After this initial phase, retrying can slow down.
 */
const retryPolicy = capDelay(
  date.milliseconds({ seconds: 10 }),
  exponentialBackoff(date.milliseconds({ seconds: 0.2 })),
);

export const invokeQuery: <N extends ProcedureName<ReadOnlyProcedures>>(
  name: N,
) => (
  ...args: Args<ProcedureArgs<ReadOnlyProcedures, N>>
) => task.Task<ProcedureResult<ReadOnlyProcedures, N>> = rpc.invokeProcedure(socket, {
  retryPolicy,
}) as $Inexpressible;

export const useQuery = rpc.makeUseProcedure<ReadOnlyProcedures>(socket, { retryPolicy });

export const useCachedQuery = rpc.makeUseCachedProcedure<ReadOnlyProcedures>(socket, {
  retryPolicy,
});
